import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { isLocked } from '../../lib/workspaces';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import { AddCompetitorModal } from '../../components/AddCompetitorModal';
import { DeleteModal } from '../../components/DeleteModal';
import { InfoTooltip } from '../../components/InfoTooltip';
import type { Competitor, PromptResponse, SuggestedCompetitor } from '../../types/database';

type PromptResponseMentionOnly = Pick<PromptResponse, 'competitors_mentioned'>;

type MentionEntry = {
  domain?: unknown;
  mentioned?: unknown;
};

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const extractBrandName = (domain: string): string => {
  const normalized = normalizeDomain(domain);
  const base = normalized.split('.')[0] || normalized;
  return base.charAt(0).toUpperCase() + base.slice(1);
};

export function Competitors() {
  const MAX_COMPETITORS = 10;
  const PAGE_SIZE = 10;
  const { workspace } = useAuthStore();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemovingCompetitor, setIsRemovingCompetitor] = useState(false);
  const [competitorToRemove, setCompetitorToRemove] = useState<Competitor | null>(null);
  const [isDiscoveringSuggestions, setIsDiscoveringSuggestions] = useState(false);
  const [competitorPage, setCompetitorPage] = useState(1);
  const autoDiscoveryAttemptedForWorkspace = useRef<string | null>(null);

  const { data: competitors = [] } = useQuery({
    queryKey: ['competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as Competitor[];
    },
    enabled: !!workspace?.id,
  });

  const { data: suggestedCompetitors = [] } = useQuery({
    queryKey: ['suggested_competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('suggested_competitors')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as SuggestedCompetitor[];
    },
    enabled: !!workspace?.id,
  });

  const competitorDomains = useMemo(
    () => new Set(competitors.map((item) => normalizeDomain(item.domain))),
    [competitors],
  );

  const filteredSuggestedCompetitors = useMemo(
    () =>
      suggestedCompetitors.filter(
        (item) => !competitorDomains.has(normalizeDomain(item.domain)),
      ),
    [suggestedCompetitors, competitorDomains],
  );

  const { data: promptResponses = [] } = useQuery({
    queryKey: ['prompt_responses_mentions', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_responses')
        .select('competitors_mentioned')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PromptResponseMentionOnly[];
    },
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    const discoverSuggestedCompetitors = async () => {
      // A Locked workspace's suggestions are hidden, not missing.
      if (!workspace?.id || !workspace.brand_url || isLocked(workspace)) return;
      if (autoDiscoveryAttemptedForWorkspace.current === workspace.id) return;
      if (filteredSuggestedCompetitors.length > 0) {
        autoDiscoveryAttemptedForWorkspace.current = workspace.id;
        return;
      }

      autoDiscoveryAttemptedForWorkspace.current = workspace.id;
      setIsDiscoveringSuggestions(true);
      try {
        const { error } = await supabase.functions.invoke('find-suggested-competitors', {
          body: {
            workspace_id: workspace.id,
            brand_url: workspace.brand_url,
          },
        });

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['suggested_competitors', workspace.id] });
      } catch (error) {
        console.error('Error discovering suggested competitors:', error);
      } finally {
        setIsDiscoveringSuggestions(false);
      }
    };

    discoverSuggestedCompetitors();
  }, [workspace, filteredSuggestedCompetitors.length, queryClient]);

  useEffect(() => {
    const duplicateSuggestedIds = suggestedCompetitors
      .filter((item) => competitorDomains.has(normalizeDomain(item.domain)))
      .map((item) => item.id);

    if (!workspace?.id || duplicateSuggestedIds.length === 0) return;

    const cleanupDuplicates = async () => {
      const { error } = await supabase
        .from('suggested_competitors')
        .delete()
        .in('id', duplicateSuggestedIds);

      if (error) {
        console.error('Error removing duplicate suggested competitors:', error);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['suggested_competitors', workspace.id] });
    };

    cleanupDuplicates();
  }, [workspace?.id, suggestedCompetitors, competitorDomains, queryClient]);

  const mentionCountsByDomain = useMemo(() => {
    const counts = new Map<string, number>();

    for (const response of promptResponses) {
      const rawMentions = response.competitors_mentioned;
      if (!Array.isArray(rawMentions)) continue;

      for (const mention of rawMentions as MentionEntry[]) {
        if (!mention || typeof mention !== 'object') continue;

        const domain = typeof mention.domain === 'string' ? normalizeDomain(mention.domain) : '';
        const mentioned = mention.mentioned === true;

        if (!domain || !mentioned) continue;
        counts.set(domain, (counts.get(domain) || 0) + 1);
      }
    }

    return counts;
  }, [promptResponses]);

  const hasCompetitorPagination = competitors.length > PAGE_SIZE;
  const totalCompetitorPages = Math.max(1, Math.ceil(competitors.length / PAGE_SIZE));

  useEffect(() => {
    if (competitorPage > totalCompetitorPages) {
      setCompetitorPage(totalCompetitorPages);
    }
  }, [competitorPage, totalCompetitorPages]);

  const paginatedCompetitors = useMemo(() => {
    const start = (competitorPage - 1) * PAGE_SIZE;
    return competitors.slice(start, start + PAGE_SIZE);
  }, [competitors, competitorPage]);

  const handleAddCompetitor = async (rawUrl: string) => {
    if (!workspace?.id) return;

    setIsAddingCompetitor(true);
    try {
      if (competitors.length >= MAX_COMPETITORS) {
        toast.error(`You can track up to ${MAX_COMPETITORS} competitors`);
        return;
      }

      const domain = normalizeDomain(rawUrl);

      if (!domain || !domain.includes('.')) {
        toast.error('Please enter a valid competitor URL');
        return;
      }

      const exists = competitors.some((item) => normalizeDomain(item.domain) === domain);
      if (exists) {
        toast.error('This competitor is already in your list');
        return;
      }

      const { error: insertError } = await supabase.from('competitors').insert({
        workspace_id: workspace.id,
        name: extractBrandName(domain),
        domain,
        favicon_url: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
      });

      if (insertError) throw insertError;

      const matchedSuggestion = suggestedCompetitors.find(
        (item) => normalizeDomain(item.domain) === domain
      );

      if (matchedSuggestion) {
        await supabase.from('suggested_competitors').delete().eq('id', matchedSuggestion.id);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['competitors', workspace.id] }),
        queryClient.invalidateQueries({ queryKey: ['suggested_competitors', workspace.id] }),
      ]);

      setIsAddModalOpen(false);
      toast.success('Competitor added');
    } catch (error: any) {
      console.error('Error adding competitor:', error);
      toast.error(error?.message || 'Failed to add competitor');
    } finally {
      setIsAddingCompetitor(false);
    }
  };

  const handleAddSuggestedCompetitor = async (suggestion: SuggestedCompetitor) => {
    if (!workspace?.id) return;

    try {
      if (competitors.length >= MAX_COMPETITORS) {
        toast.error(`You can track up to ${MAX_COMPETITORS} competitors`);
        return;
      }

      const domain = normalizeDomain(suggestion.domain);
      const exists = competitors.some((item) => normalizeDomain(item.domain) === domain);
      if (exists) {
        await supabase.from('suggested_competitors').delete().eq('id', suggestion.id);
        await queryClient.invalidateQueries({ queryKey: ['suggested_competitors', workspace.id] });
        return;
      }

      const { error: insertError } = await supabase.from('competitors').insert({
        workspace_id: workspace.id,
        name: suggestion.name,
        domain,
        favicon_url:
          suggestion.favicon_url ||
          `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
      });
      if (insertError) throw insertError;

      const { error: deleteError } = await supabase
        .from('suggested_competitors')
        .delete()
        .eq('id', suggestion.id);
      if (deleteError) throw deleteError;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['competitors', workspace.id] }),
        queryClient.invalidateQueries({ queryKey: ['suggested_competitors', workspace.id] }),
      ]);

      toast.success('Competitor added from suggestions');
    } catch (error: any) {
      console.error('Error adding suggested competitor:', error);
      toast.error(error?.message || 'Failed to add suggested competitor');
    }
  };

  const openRemoveCompetitorModal = (competitor: Competitor) => {
    setCompetitorToRemove(competitor);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmRemoveCompetitor = async () => {
    if (!workspace?.id || !competitorToRemove) return;

    setIsRemovingCompetitor(true);
    try {
      const { error } = await supabase.from('competitors').delete().eq('id', competitorToRemove.id);
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['competitors', workspace.id] });
      setIsDeleteModalOpen(false);
      setCompetitorToRemove(null);
      toast.success('Competitor removed');
    } catch (error: any) {
      console.error('Error removing competitor:', error);
      toast.error(error?.message || 'Failed to remove competitor');
    } finally {
      setIsRemovingCompetitor(false);
    }
  };

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="overview-header" style={{ marginBottom: '1.5rem' }}>
        <div className="overview-header-left">
          <div className="overview-panel-title-block">
            <h1>Competitors</h1>
            <div className="spacer-xtiny"></div>
            <div className="text-size-small text-colour-alt-grey">See all tracked and suggested brand competitors here</div>
          </div>
        </div>
        <button
          className="button is-small w-button"
          onClick={() => setIsAddModalOpen(true)}
          disabled={competitors.length >= MAX_COMPETITORS}
          title={competitors.length >= MAX_COMPETITORS ? `Maximum ${MAX_COMPETITORS} competitors` : undefined}
        >
          Add Competitor
        </button>
      </div>

      <div className="dashboard-panel" style={{ marginBottom: '1.5rem' }}>
        <div className="dashboard-panel-header" style={{ padding: '1rem 1.75rem' }}>
          <div className="table-title-wrapper is-left">
            <div className="dashboard-panel-title">
              Tracked Competitors <span className="number-pill">{competitors.length}</span>
            </div>
            <InfoTooltip text="Competitors currently tracked for mentions across AI prompt responses." />
          </div>
        </div>
        <div className="dashboard-panel-body is-table">
          <div className="table-scroll-wrap is-tracked-competitors-table">
          <table className="table-component competitors-table">
            <thead className="table-header">
              <tr className="table-row is-header-row">
                <th className="table-head is-name">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brand</div>
                </th>
                <th className="table-head is-date-added">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Added</div>
                </th>
                <th className="table-head is-mentions">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey overview-table-head-label">
                    <span>Total Mentions</span>
                    <InfoTooltip text="All-time mentions for this brand." placement="bottom" />
                  </div>
                </th>
                <th className="table-head is-action">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">
                    Action
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {competitors.length === 0 ? (
                <tr className="table-row" style={{ cursor: 'default' }}>
                  <td className="table-cell" colSpan={4} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                    <div className="text-size-small text-colour-light-grey">
                      Add competitors to start tracking mentions.
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedCompetitors.map((competitor, index) => {
                  const domain = normalizeDomain(competitor.domain);
                  const mentionCount = mentionCountsByDomain.get(domain) || 0;
                  const isLastVisibleRow = index === paginatedCompetitors.length - 1;

                  return (
                    <tr
                      key={competitor.id}
                      className="table-row"
                      style={{
                        cursor: 'default',
                        borderBottom: isLastVisibleRow ? 'none' : '1px solid var(--light-grey-outline)',
                      }}
                    >
                      <td className="table-cell is-name">
                        <div className="competitors-name-cell-content">
                          <div
                            className="site-icon"
                            style={
                              competitor.favicon_url
                                ? { backgroundImage: `url(${competitor.favicon_url})`, flexShrink: 0 }
                                : { flexShrink: 0 }
                            }
                          />
                          <div className="competitors-name-cell-text">
                            <div className="text-size-small text-weight-semibold">{competitor.name}</div>
                            <div className="text-size-small text-colour-alt-grey">{domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell is-date-added">
                        <div className="text-size-xsmall text-colour-alt-grey">{formatDate(competitor.created_at)}</div>
                      </td>
                      <td className="table-cell is-mentions">
                        <div className={`table-count-badge ${mentionCount === 0 ? 'is-zero' : ''}`}>{mentionCount.toLocaleString()}</div>
                        <button
                          type="button"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          onClick={() => navigate(`/mentions?competitor=${encodeURIComponent(domain)}`)}
                          style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                        >
                          View
                        </button>
                      </td>
                      <td className="table-cell is-action">
                        <button
                          type="button"
                          className="remove-icon-holder"
                          onClick={() => openRemoveCompetitorModal(competitor)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            marginLeft: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          aria-label={`Remove ${competitor.name}`}
                          title={`Remove ${competitor.name}`}
                        >
                          <span className="embed-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
              {hasCompetitorPagination && (
                <tr className="table-row is-pagination-row">
                  <td className="table-cell is-pagination" colSpan={4} style={{ borderTop: '1px solid var(--light-grey-outline)' }}>
                    <div className="pagination">
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setCompetitorPage((page) => Math.max(1, page - 1))}
                        disabled={competitorPage === 1}
                      >
                        Previous
                      </button>
                      <div className="text-size-small text-weight-medium text-colour-alt-grey">
                        Page {competitorPage} of {totalCompetitorPages}
                      </div>
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setCompetitorPage((page) => Math.min(totalCompetitorPages, page + 1))}
                        disabled={competitorPage === totalCompetitorPages}
                      >
                        Next
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div className="dashboard-panel">
        <div className="dashboard-panel-header" style={{ padding: '1rem 1.75rem' }}>
          <div className="table-title-wrapper is-left">
            <div className="dashboard-panel-title">Suggested Competitors</div>
            <InfoTooltip text="Additional competitors suggested during dashboard setup." />
          </div>
        </div>
        <div className="dashboard-panel-body is-table">
          {isDiscoveringSuggestions && (
            <div className="text-size-small text-colour-alt-grey" style={{ padding: '0.75rem 1.75rem' }}>
              Searching for suggested competitors...
            </div>
          )}
          <div className="table-scroll-wrap is-suggested-competitors-table">
          <table className="table-component competitors-table competitors-table--suggested">
            <thead className="table-header">
              <tr className="table-row is-header-row">
                <th className="table-head is-name">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brand</div>
                </th>
                <th className="table-head is-date-found">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Date Found</div>
                </th>
                <th className="table-head is-action-suggested">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">
                    Action
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {filteredSuggestedCompetitors.length === 0 ? (
                <tr className="table-row" style={{ cursor: 'default' }}>
                  <td className="table-cell" colSpan={3} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                    <div className="text-size-small text-colour-light-grey">
                      No suggestions yet.
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSuggestedCompetitors.map((competitor, index) => {
                  const domain = normalizeDomain(competitor.domain);
                  const isLastSuggestedRow = index === filteredSuggestedCompetitors.length - 1;
                  return (
                    <tr
                      key={competitor.id}
                      className="table-row"
                      style={{
                        cursor: 'default',
                        borderBottom: isLastSuggestedRow ? 'none' : '1px solid var(--light-grey-outline)',
                      }}
                    >
                      <td className="table-cell is-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                          <div
                            className="site-icon"
                            style={
                              competitor.favicon_url
                                ? { backgroundImage: `url(${competitor.favicon_url})`, flexShrink: 0 }
                                : { flexShrink: 0 }
                            }
                          />
                          <div>
                            <div className="text-size-small text-weight-semibold">{competitor.name}</div>
                            <div className="text-size-small text-colour-alt-grey">{domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell is-date-found">
                        <div className="text-size-small text-colour-alt-grey">{formatDate(competitor.created_at)}</div>
                      </td>
                      <td className="table-cell is-action-suggested">
                        <button
                          type="button"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          onClick={() => handleAddSuggestedCompetitor(competitor)}
                          disabled={competitors.length >= MAX_COMPETITORS}
                          style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                        >
                          + Add
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <AddCompetitorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onConfirm={handleAddCompetitor}
        loading={isAddingCompetitor}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isRemovingCompetitor) {
            setIsDeleteModalOpen(false);
            setCompetitorToRemove(null);
          }
        }}
        onConfirm={handleConfirmRemoveCompetitor}
        title="Remove competitor?"
        message={
          competitorToRemove
            ? `${competitorToRemove.name} will no longer be tracked in your competitor list.`
            : 'This competitor will no longer be tracked in your competitor list.'
        }
        confirmText="Yes, remove"
        cancelText="No, cancel"
        loading={isRemovingCompetitor}
      />

      <div className="spacer-large"></div>
    </div>
  );
}
